import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Skeleton } from '@mui/material';
import { colors, IconButton, Tooltip, Typography } from '@sweep-io/sweep-design';
import { Close, Download } from '@sweep-io/sweep-design/dist/icons';
import { useTableArtifacts } from './useTableArtifacts';
import { useAiAgentApiFacade } from '../../../apis/facades/useAiAgentApiFacade';
import { MarkdownWrapper } from '../MarkdownWrapper';
import { DataTableVariant } from '../table/TableTypes';
import { telemetry } from '../../../telemetry';
import { useResizeDrag } from '../../../hooks/useResizeDrag';
import { useArtifactPanelWidth } from '../../../hooks/useArtifactPanelWidth';

/**
 * Parse a markdown table and convert it to CSV format
 */
function markdownTableToCsv(markdown: string): string {
  const lines = markdown.trim().split('\n');
  const csvRows: string[] = [];

  for (const line of lines) {
    // Skip separator lines (containing only |, -, and spaces)
    if (/^\|[\s\-:|]+\|$/.test(line.trim())) {
      continue;
    }

    // Parse table row
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const cells = line
        .trim()
        .slice(1, -1) // Remove leading and trailing |
        .split('|')
        .map((cell) => {
          const trimmed = cell.trim();
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (trimmed.includes(',') || trimmed.includes('"') || trimmed.includes('\n')) {
            return `"${trimmed.replace(/"/g, '""')}"`;
          }
          return trimmed;
        });

      csvRows.push(cells.join(','));
    }
  }

  return csvRows.join('\n');
}

/**
 * Trigger a file download in the browser
 */
function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const TableSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
    <Skeleton variant="rectangular" height={40} />
    <Skeleton variant="rectangular" height={32} />
    <Skeleton variant="rectangular" height={32} />
    <Skeleton variant="rectangular" height={32} />
    <Skeleton variant="rectangular" height={32} />
  </Box>
);

const TableErrorState = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 4,
      height: '100%',
    }}
  >
    <Typography variant="body" color={colors.blush[600]}>
      Failed to load table data. Please try again.
    </Typography>
  </Box>
);

export const TableArtifactPanel: React.FC = () => {
  const {
    isTablePanelOpen,
    currentTableId,
    closeTablePanel,
    tableArtifacts,
    isLoading,
    error,
    setTableData,
    setLoading,
    setError,
  } = useTableArtifacts();

  const { get_table_artifact } = useAiAgentApiFacade();
  const fetchingRef = useRef<string | null>(null);
  const { panelWidth, updatePanelWidth, minWidth, maxWidth } = useArtifactPanelWidth();

  const {
    isResizing,
    isHovering: isHoveringHandle,
    handleResizeStart,
    handleMouseEnter,
    handleMouseLeave,
  } = useResizeDrag({
    onResize: updatePanelWidth,
    minValue: minWidth,
    maxValue: maxWidth,
    calculateDiff: (e: MouseEvent, startX: number) => {
      return startX - e.clientX;
    },
  });

  // Fetch table data when panel opens
  useEffect(() => {
    if (!isTablePanelOpen || !currentTableId) {
      return;
    }

    // Already have the data cached
    if (tableArtifacts[currentTableId]) {
      return;
    }

    // Already fetching this table
    if (fetchingRef.current === currentTableId) {
      return;
    }

    const fetchTableData = async () => {
      fetchingRef.current = currentTableId;
      setLoading(true);
      setError(null);

      try {
        const data = await get_table_artifact(currentTableId);
        setTableData(currentTableId, data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch table'));
        telemetry.captureError(err);
      } finally {
        setLoading(false);
        fetchingRef.current = null;
      }
    };

    fetchTableData();
  }, [
    isTablePanelOpen,
    currentTableId,
    tableArtifacts,
    get_table_artifact,
    setTableData,
    setLoading,
    setError,
  ]);

  const tableData = currentTableId ? tableArtifacts[currentTableId] : null;

  const handleDownloadCsv = useCallback(() => {
    if (!tableData?.markdown) return;

    try {
      const csvContent = markdownTableToCsv(tableData.markdown);
      const filename = `table-${currentTableId || 'data'}.csv`;
      downloadCsv(csvContent, filename);
    } catch (err) {
      telemetry.captureError(err);
    }
  }, [tableData, currentTableId]);

  if (!isTablePanelOpen) {
    return null;
  }

  const renderContent = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (error || !tableData) {
      return <TableErrorState />;
    }

    return (
      <Box
        sx={{
          p: 2,
          height: '100%',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: colors.grey[300],
            borderRadius: '3px',
          },
        }}
      >
        <MarkdownWrapper
          content={tableData.markdown}
          removeMargins={true}
          dataTableVariant={DataTableVariant.medium}
          shouldShowTableCopyButtons={true}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${panelWidth}px`,
        minWidth: `${minWidth}px`,
        maxWidth: `${maxWidth}px`,
        height: '100%',
        backgroundColor: colors.white,
        borderLeft: `1px solid ${colors.grey[300]}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      {/* Resize handle */}
      <Box
        className="table-panel-resize-handle"
        sx={{
          position: 'absolute',
          left: '-2px',
          top: 0,
          bottom: 0,
          width: '6px',
          cursor: 'col-resize',
          zIndex: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover .resize-indicator': {
            opacity: 1,
            backgroundColor: colors.blue[500],
          },
        }}
        onMouseDown={(e) => handleResizeStart(e, panelWidth)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Visual indicator - the actual blue line */}
        <Box
          className="resize-indicator"
          sx={{
            width: '2px',
            height: '100%',
            transition: 'opacity 0.2s ease-in-out, background-color 0.2s ease-in-out',
            backgroundColor: isHoveringHandle || isResizing ? colors.blue[500] : 'transparent',
            opacity: isHoveringHandle || isResizing ? 1 : 0,
          }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${colors.grey[300]}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="h4">Table Data</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {tableData && (
            <Tooltip title="Download CSV" placement="top">
              <IconButton variant="flat" size="small" onClick={handleDownloadCsv}>
                <Download />
              </IconButton>
            </Tooltip>
          )}
          <IconButton variant="flat" size="small" onClick={closeTablePanel}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>{renderContent()}</Box>
    </Box>
  );
};
